import React, { useEffect, useState } from "react";
import { fetchFixtureHighlights, fetchTeamHighlights } from "../api";

const toVideos = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.videos)) return payload.videos;
  return [];
};

const formatPublished = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

function PlayOverlay() {
  return (
    <span
      style={{
        position: "absolute",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, rgba(0,0,0,0.06), rgba(0,0,0,0.42))",
      }}
    >
      <span
        style={{
          width: 54,
          height: 54,
          borderRadius: 999,
          background: "rgba(255,255,255,0.92)",
          color: "#111827",
          display: "grid",
          placeItems: "center",
          fontSize: 22,
          fontWeight: 900,
          boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
        }}
      >
        ▶
      </span>
    </span>
  );
}

function VideoCard({ video, featured = false, dark = false }) {
  const title = video?.title || "Highlights";
  const url = video?.video_url;
  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.68)" : "rgba(17,24,39,0.62)";
  const border = dark ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(15,23,42,0.1)";
  const bg = dark ? "rgba(255,255,255,0.04)" : "#fff";

  const openVideo = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      type="button"
      onClick={openVideo}
      disabled={!url}
      style={{
        padding: 0,
        textAlign: "left",
        border,
        borderRadius: 8,
        background: bg,
        overflow: "hidden",
        cursor: url ? "pointer" : "default",
        color: text,
        display: "grid",
        gridTemplateColumns: featured ? "repeat(auto-fit, minmax(220px, 1fr))" : "120px minmax(0, 1fr)",
        minHeight: featured ? 220 : 92,
        boxShadow: featured ? "0 18px 42px rgba(0,0,0,0.18)" : "none",
      }}
    >
      <span style={{ position: "relative", minHeight: featured ? 220 : 92, background: "#020617" }}>
        {video?.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            loading="lazy"
          />
        ) : (
          <span style={{ display: "grid", placeItems: "center", height: "100%", color: "#94a3b8", fontWeight: 800 }}>
            Highlights
          </span>
        )}
        <PlayOverlay />
      </span>
      <span style={{ padding: featured ? 18 : 10, display: "grid", alignContent: "center", gap: 7 }}>
        <span style={{ fontSize: 12, color: muted, fontWeight: 700 }}>
          {video?.channel_name || "YouTube"} {video?.published_at ? `• ${formatPublished(video.published_at)}` : ""}
        </span>
        <span style={{ fontSize: featured ? 22 : 14, lineHeight: 1.18, fontWeight: 900 }}>{title}</span>
        {featured && video?.description ? (
          <span style={{ fontSize: 13, color: muted, lineHeight: 1.45 }}>
            {String(video.description).slice(0, 180)}
            {String(video.description).length > 180 ? "..." : ""}
          </span>
        ) : null}
        <span style={{ fontSize: 13, color: dark ? "#86efac" : "#15803d", fontWeight: 800 }}>
          {featured ? "Watch highlights" : "Open on YouTube"}
        </span>
      </span>
    </button>
  );
}

export default function HighlightVideosSection({
  mode = "fixture",
  fixtureId,
  teamName,
  title = "Highlights",
  limit,
  sinceDays,
  dark = false,
  featured = true,
  emptyText = "",
  hideWhenEmpty = true,
}) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const key = mode === "fixture" ? fixtureId : teamName;
    if (!key) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const payload =
          mode === "fixture"
            ? await fetchFixtureHighlights(fixtureId, { limit: limit || 4, since_days: sinceDays || 45 })
            : await fetchTeamHighlights(teamName, { limit: limit || 18, since_days: sinceDays || 90 });
        if (!cancelled) setVideos(toVideos(payload));
      } catch {
        if (!cancelled) setVideos([]);
      } finally {
        if (!cancelled) {
          setLoaded(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fixtureId, limit, mode, sinceDays, teamName]);

  if (!loaded && !loading) return null;
  if (!loading && loaded && !videos.length && hideWhenEmpty) return null;

  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.66)" : "rgba(17,24,39,0.62)";
  const first = videos[0];
  const rest = videos.slice(1);

  return (
    <section style={{ margin: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: text, fontSize: featured ? 22 : 18 }}>{title}</h2>
          <div style={{ marginTop: 2, color: muted, fontSize: 13 }}>Cached video highlights from trusted channels.</div>
        </div>
        {loading ? <span style={{ color: muted, fontSize: 12 }}>Loading...</span> : null}
      </div>

      {!videos.length ? (
        <div style={{ color: muted, fontSize: 13 }}>{emptyText || "No highlights matched yet."}</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {first ? <VideoCard video={first} featured={featured} dark={dark} /> : null}
          {rest.length ? (
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              {rest.map((video, idx) => (
                <VideoCard key={video.id || video.external_video_id || idx} video={video} dark={dark} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
